import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isRouteErrorResponse, UNSAFE_DataRouterStateContext, useLocation, useRouteLoaderData } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useAuth } from '@/providers/auth';
import { useAnalyticsUserProfile, useAnalyticsUserProfileLoading } from '@/providers/analytics-user-profile';
import { useDataLayer } from '@/hooks/use-data-layer';
import { usePageAnalyticsTitle, usePageContentGroup } from '@/hooks/use-page-content-group';
import { DEFAULT_SITE_NAME_KEY } from '@/components/seo-meta';
import { buildSeoTitle } from '@/lib/page-seo';
import { readPrimaryShoppingPillarClient } from '@/lib/primary-shopping-category.client';
import { resolveBrowsingPreference } from '@/lib/analytics/browsing-preference';
import { stripRegistrationReferralParamsFromUrl } from '@/lib/registration-referral';
import { loadGoogleTagManager, type GoogleTagManagerConfig } from '@/lib/google-tag-manager';
import type { DataLayerEvent, DataLayerEventName } from './data-layer';
import { hasGoogleTagManagerStarted, markGoogleTagManagerStarted, markPendingPageView } from './data-layer-bootstrap';
import {
    buildLogStateUserInfo,
    isCookiebotConsentReady,
    isCookiebotPresent,
    resolveAnalyticsProfileForLogState,
    resolveLogStateConsentState,
} from './log-state-user-info';
import {
    consumePendingLogin,
    getPendingLoginHoldKey,
    PENDING_LOGIN_HOLD_FALLBACK_MS,
    peekPendingLogin,
    releasePendingLoginHold,
    shouldHoldAuthLifecycle,
} from './pending-login';

/**
 * Safety net: never block analytics permanently while waiting for Cookiebot.
 * If no consent event arrives (script blocked, load failure, or Cookiebot injected
 * only by a later-loading tag), proceed anyway so `log_state`/`page_view` still fire.
 */
const CONSENT_READY_FALLBACK_MS = 4000;

/** GTM loads Cookiebot, so wait until Cookiebot answers (or the fallback). No GTM → ready now. */
function isInitialConsentReady(gtmConfigured: boolean): boolean {
    if (!gtmConfigured) {
        return true;
    }
    return isCookiebotPresent() && isCookiebotConsentReady();
}

const DECLARATIVE_EVENT_NAMES = new Set<DataLayerEventName>([
    'tab_click',
    'home_box_click',
    'nav_cta_click',
    'register_click',
    'share',
    'phone_click',
    'mail_click',
    'get_direction_click',
    'eataly_club_click',
    'book_a_table_click',
    'opentable_widget_opened',
    'menu_click',
    'restaurant_click',
    'wine_list_click',
    'store_click',
    'store_outbound_click',
    'flyer_click',
]);

/** Custom click events that must include an absolute destination URL in the data layer. */
const CLICK_URL_EVENT_NAMES = new Set<DataLayerEventName>([
    'book_a_table_click',
    'eataly_club_click',
    'flyer_click',
    'get_direction_click',
    'home_box_click',
    'mail_click',
    'menu_click',
    'nav_cta_click',
    'phone_click',
    'register_click',
    'restaurant_click',
    'store_click',
    'store_outbound_click',
    'wine_list_click',
]);
function getLanguageAndCountry(localeId: string, siteId: string): { language: string; country: string } {
    const [language = '', localeCountry = ''] = localeId.replace('_', '-').split('-');
    const country = localeCountry || (siteId.toUpperCase().startsWith('US') ? 'US' : 'IT');
    return { language: language.toLowerCase(), country: country.toLowerCase() };
}

function getClickText(element: HTMLElement): string {
    return element.textContent?.replace(/\s+/g, ' ').trim() || element.getAttribute('aria-label')?.trim() || '';
}

function isDirectionsHref(href: string): boolean {
    const normalized = href.toLowerCase();
    return (
        normalized.startsWith('geo:') ||
        normalized.includes('google.com/maps') ||
        normalized.includes('maps.google.') ||
        normalized.includes('maps.app.goo.gl') ||
        normalized.includes('maps.apple.com')
    );
}

function resolveAbsoluteClickUrl(href: string, baseUrl: string): string | undefined {
    const trimmed = href.trim();
    if (!trimmed || trimmed === '#') {
        return undefined;
    }
    try {
        return new URL(trimmed, baseUrl).href;
    } catch {
        return undefined;
    }
}

function resolveClickUrlFromElement(element: HTMLElement): string {
    const baseUrl = window.location.href;
    const link = element.closest('a[href]');
    if (link instanceof HTMLAnchorElement) {
        const href = link.getAttribute('href');
        if (href) {
            const absolute = resolveAbsoluteClickUrl(href, baseUrl);
            if (absolute) {
                return absolute;
            }
        }
    }
    return baseUrl;
}

type ClickUrlTrackInput = {
    event: DataLayerEventName;
    event_type: DataLayerEvent['event_type'];
    click_url?: string;
} & Record<string, unknown>;

function withClickUrl(event: ClickUrlTrackInput, element: HTMLElement): DataLayerEvent {
    if (!CLICK_URL_EVENT_NAMES.has(event.event)) {
        return event as DataLayerEvent;
    }

    const existing = typeof event.click_url === 'string' ? event.click_url.trim() : '';
    if (existing) {
        const normalized = resolveAbsoluteClickUrl(existing, window.location.href);
        if (normalized) {
            return { ...event, click_url: normalized } as DataLayerEvent;
        }
    }

    return { ...event, click_url: resolveClickUrlFromElement(element) } as DataLayerEvent;
}

function readDeclarativeEvent(element: HTMLElement): DataLayerEvent | null {
    const eventName = element.dataset.analyticsEvent as DataLayerEventName | undefined;
    if (!eventName || !DECLARATIVE_EVENT_NAMES.has(eventName)) {
        return null;
    }

    let payload: Record<string, unknown> = {};
    const serializedPayload = element.dataset.analyticsPayload;
    if (serializedPayload) {
        try {
            const parsed = JSON.parse(serializedPayload) as unknown;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                payload = parsed as Record<string, unknown>;
            }
        } catch {
            // Invalid declarative analytics must never interrupt the shopper interaction.
        }
    }

    return {
        event: eventName,
        event_type: 'ga4_ce',
        ...payload,
    } as DataLayerEvent;
}

const DEFAULT_HTTP_STATUS_CODE = 200;

/** Deepest loader/action error status for the current route tree; 200 when none. */
export function resolvePageHttpStatusCode(
    matches: ReadonlyArray<{ id: string }>,
    errors: Record<string, unknown> | null | undefined
): number {
    if (!errors) {
        return DEFAULT_HTTP_STATUS_CODE;
    }

    for (let index = matches.length - 1; index >= 0; index -= 1) {
        const routeId = matches[index]?.id;
        if (!routeId || !(routeId in errors)) {
            continue;
        }

        const error = errors[routeId];
        if (isRouteErrorResponse(error) || error instanceof Response) {
            return error.status;
        }

        return 500;
    }

    return DEFAULT_HTTP_STATUS_CODE;
}

function usePageHttpStatusCode(): number {
    const routerState = useContext(UNSAFE_DataRouterStateContext);

    return useMemo(() => {
        if (!routerState) {
            return DEFAULT_HTTP_STATUS_CODE;
        }

        const matches = routerState.matches.map((match) => ({ id: match.route.id }));
        return resolvePageHttpStatusCode(matches, routerState.errors);
    }, [routerState]);
}

/**
 * Global dataLayer lifecycle tracker. It owns events that are tied to the SPA
 * itself (user state and page views) and contact links that can appear in
 * Page Designer content outside React-owned components.
 */
export function DataLayerTracker() {
    const location = useLocation();
    const contentGroup = usePageContentGroup();
    const analyticsPageTitle = usePageAnalyticsTitle();
    const { t } = useTranslation('common');
    const { site } = useSite();
    const auth = useAuth();
    const analyticsProfile = useAnalyticsUserProfile();
    const isAnalyticsProfileLoading = useAnalyticsUserProfileLoading();
    const dataLayer = useDataLayer();
    const rootData = useRouteLoaderData('root') as { googleTagManager?: GoogleTagManagerConfig | null } | undefined;
    const googleTagManager = rootData?.googleTagManager ?? null;
    const httpStatusCode = usePageHttpStatusCode();
    const trackedPageRef = useRef<string | null>(null);
    const trackedUserRef = useRef<string | null>(null);
    const [cookieConsentState, setCookieConsentState] = useState(resolveLogStateConsentState);
    const [isConsentReady, setIsConsentReady] = useState(() => isInitialConsentReady(Boolean(googleTagManager)));
    const [pendingLoginHoldReleased, setPendingLoginHoldReleased] = useState(false);
    const pendingLoginHoldKey = getPendingLoginHoldKey();

    useEffect(() => {
        const syncConsentState = () => {
            setCookieConsentState(resolveLogStateConsentState());
            setIsConsentReady(true);
        };
        window.addEventListener('CookiebotOnConsentReady', syncConsentState);
        window.addEventListener('CookiebotOnAccept', syncConsentState);
        window.addEventListener('CookiebotOnDecline', syncConsentState);
        const fallbackTimer = window.setTimeout(() => setIsConsentReady(true), CONSENT_READY_FALLBACK_MS);
        return () => {
            window.removeEventListener('CookiebotOnConsentReady', syncConsentState);
            window.removeEventListener('CookiebotOnAccept', syncConsentState);
            window.removeEventListener('CookiebotOnDecline', syncConsentState);
            window.clearTimeout(fallbackTimer);
        };
    }, []);

    useEffect(() => {
        if (!pendingLoginHoldKey || pendingLoginHoldReleased) {
            return;
        }
        const holdTimer = window.setTimeout(() => {
            releasePendingLoginHold();
            setPendingLoginHoldReleased(true);
        }, PENDING_LOGIN_HOLD_FALLBACK_MS);
        return () => window.clearTimeout(holdTimer);
    }, [pendingLoginHoldKey, pendingLoginHoldReleased]);

    useLayoutEffect(() => {
        const pageKey = `${location.pathname}${location.search}${location.hash}`;
        markPendingPageView(pageKey);

        if (googleTagManager && !hasGoogleTagManagerStarted()) {
            loadGoogleTagManager(googleTagManager.scriptUrl);
            markGoogleTagManagerStarted();
        }

        if (!dataLayer.canTrack || auth === undefined || !isConsentReady) {
            return;
        }

        const analyticsProfileForLogState = resolveAnalyticsProfileForLogState(auth, analyticsProfile);
        const holdAuthLifecycle = shouldHoldAuthLifecycle({
            userType: auth.userType,
            isProfileLoading: isAnalyticsProfileLoading,
            hasAnalyticsProfile: Boolean(analyticsProfileForLogState),
        });
        if (isAnalyticsProfileLoading || holdAuthLifecycle) {
            return;
        }

        const userKey = JSON.stringify({
            customerId: auth.customerId,
            encUserId: auth.encUserId,
            userType: auth.userType,
            firstName: auth.firstName,
            lastName: auth.lastName,
            consentState: cookieConsentState,
            analyticsProfile: analyticsProfileForLogState,
        });
        if (trackedUserRef.current !== userKey) {
            trackedUserRef.current = userKey;
            const browsingPreference = resolveBrowsingPreference(readPrimaryShoppingPillarClient(site.id));
            dataLayer.track({
                event: 'log_state',
                user_info: buildLogStateUserInfo(auth, analyticsProfileForLogState, browsingPreference),
                event_type: 'ga4_ce',
            });

            if (auth.userType === 'registered') {
                const pendingLogin = consumePendingLogin();
                if (pendingLogin) {
                    const emailAddress = pendingLogin.email_address ?? analyticsProfileForLogState?.email_address;
                    dataLayer.track({
                        event: pendingLogin.event ?? 'login',
                        method: pendingLogin.method,
                        target: pendingLogin.target,
                        user_data: emailAddress ? { email_address: emailAddress } : {},
                        event_type: 'ga4_ce',
                    });
                }
            } else if (peekPendingLogin()) {
                consumePendingLogin();
            }
        }

        if (trackedPageRef.current === pageKey) {
            return;
        }
        trackedPageRef.current = pageKey;

        const { language, country } = getLanguageAndCountry(dataLayer.localeId, dataLayer.siteId);
        // Prefer the route-resolved title so SPA navigations never report the previous page's
        // `document.title` before the new head tags are committed. Fall back to the DOM for
        // routes that don't expose one.
        const pageTitle = analyticsPageTitle
            ? buildSeoTitle({
                  title: analyticsPageTitle.title,
                  rawTitle: analyticsPageTitle.rawTitle,
                  siteName: t(DEFAULT_SITE_NAME_KEY),
              })
            : document.title;
        dataLayer.track({
            event: 'page_view',
            page_location: stripRegistrationReferralParamsFromUrl(window.location.href),
            page_title: pageTitle,
            language,
            country,
            content_group: contentGroup ?? '',
            http_status_code: httpStatusCode,
            event_type: 'ga4_ce',
        });
    }, [
        analyticsPageTitle,
        analyticsProfile,
        auth,
        contentGroup,
        cookieConsentState,
        dataLayer,
        googleTagManager,
        httpStatusCode,
        isAnalyticsProfileLoading,
        isConsentReady,
        pendingLoginHoldReleased,
        location.hash,
        location.pathname,
        location.search,
        site.id,
        t,
    ]);

    useEffect(() => {
        if (!dataLayer.canTrack) {
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const declarativeElement = target.closest<HTMLElement>('[data-analytics-event]');
            if (declarativeElement) {
                const declarativeEvent = readDeclarativeEvent(declarativeElement);
                if (declarativeEvent) {
                    dataLayer.track(withClickUrl(declarativeEvent, declarativeElement));
                    return;
                }
            }

            const link = target.closest<HTMLAnchorElement>('a[href]');
            if (!link) {
                return;
            }

            const href = link.getAttribute('href') ?? '';
            const clickText = getClickText(link);
            if (href.toLowerCase().startsWith('tel:')) {
                dataLayer.track(
                    withClickUrl({ event: 'phone_click', click_text: clickText, event_type: 'ga4_ce' }, link)
                );
            } else if (href.toLowerCase().startsWith('mailto:')) {
                dataLayer.track(
                    withClickUrl({ event: 'mail_click', click_text: clickText, event_type: 'ga4_ce' }, link)
                );
            } else if (isDirectionsHref(href)) {
                dataLayer.track(
                    withClickUrl({ event: 'get_direction_click', click_text: clickText, event_type: 'ga4_ce' }, link)
                );
            } else {
                const normalizedText = clickText.toLocaleLowerCase('it-IT');
                if (
                    normalizedText === 'iscriviti' &&
                    (href.toLowerCase().includes('club') || link.closest('[data-analytics-context="eataly-club"]'))
                ) {
                    dataLayer.track(
                        withClickUrl(
                            {
                                event: 'eataly_club_click',
                                click_text: clickText,
                                event_type: 'ga4_ce',
                            },
                            link
                        )
                    );
                }
            }
        };

        // Capture phase: clicks inside portaled overlays (e.g. fullscreen nav) call
        // stopPropagation on bubble, so a bubble listener on document never runs.
        document.addEventListener('click', handleDocumentClick, true);
        return () => document.removeEventListener('click', handleDocumentClick, true);
    }, [dataLayer]);

    return null;
}
